/**
 * The Split — run the trade, then read the distribution and its rationale.
 *
 * Reuses the Session app's canonical facilitator actions verbatim (open →
 * collect → close & compute → publish); allocation differs only in how the
 * result is read, not in how it is governed.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useToolSessionStore } from '@/stores/prioritization/toolSessionStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import {
  closeAndComputeResults,
  openForParticipants,
  publishResults,
} from '../session/sessionAppActions';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { sessionPublishAuthorityLine, useSessionPublishAuthority } from '@/hooks/prioritization/useSessionPublishAuthority';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  PrimaryButton,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';
import {
  allocationAsks,
  allocationMethodName,
  readEnvelope,
  toAllocationSplit,
} from './allocateActions';

export default function SplitScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const app = getAppDefinition('allocate')!;
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);

  const fetchSessionById = useSessionStore((s) => s.fetchSessionById);
  const session = useSessionStore((s) => s.sessions.find((x) => x.id === sessionId));
  // Open, close and publish are drawn only for someone the session RPCs accept
  // (internal_session_publish_authority, migration 704) — build contract §2.8.
  const mayHost = useSessionPublishAuthority(session);
  const hostNames = useActorDisplayMap([session?.facilitatorId]);
  const hostName = session?.facilitatorId ? hostNames[session.facilitatorId] ?? null : null;
  const subscribeToSessionToolSessions = useToolSessionStore(
    (s) => s.subscribeToSessionToolSessions,
  );
  const toolSessions = useToolSessionStore((s) => s.toolSessions);
  const participantResponses = useToolSessionStore((s) => s.participantResponses);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (sessionId) void fetchSessionById(sessionId);
  }, [sessionId, fetchSessionById]);

  useEffect(() => {
    if (sessionId && session?.state === 'CRITERIA_FINALIZED') {
      void subscribeToSessionToolSessions(sessionId);
    }
  }, [sessionId, session?.state, subscribeToSessionToolSessions]);

  const submitted = useMemo(() => {
    const ids = toolSessions.filter((t) => t.sessionId === sessionId).map((t) => t.id);
    return new Set(
      participantResponses
        .filter((r) => ids.includes(r.toolSessionId) && r.isComplete)
        .map((r) => r.participantId),
    ).size;
  }, [toolSessions, participantResponses, sessionId]);

  const run = useCallback(
    async (action: () => Promise<{ success: boolean; error?: string }>) => {
      setBusy(true);
      setError(null);
      const result = await action();
      setBusy(false);
      if (!result.success) setError(result.error ?? 'Something went wrong.');
    },
    [],
  );

  const joinUrl = `${window.location.origin}/apps/session/${sessionId}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select the link text instead.');
    }
  };

  if (!session) {
    return (
      <AppShell app={app} backTo="/apps/allocate">
        <EmptyState title="Loading allocation…" />
      </AppShell>
    );
  }

  const asks = allocationAsks(session);
  const { total, unit } = readEnvelope(session);
  const split = toAllocationSplit(session, asks);
  const hasResult = split.length > 0;
  const host = mayHost ? 'you' : 'the host';

  return (
    <AppShell app={app} backTo="/apps/allocate" backLabel="Back to allocations">
      <ScreenHeading
        title={session.name}
        subtitle={`${allocationMethodName(session.selectedToolId)} · ${asks.length} asks${
          total != null ? ` · ${unit}${total.toLocaleString()} to split` : ''
        }`}
      />
      <BallLine
        ballWith={
          session.state === 'DRAFT'
            ? `${host} — open it so people can spend`
            : session.state === 'CRITERIA_FINALIZED'
              ? 'the room — they are spending now'
              : `${host} — review the split and publish`
        }
      />
      {!mayHost ? (
        <p className="mb-4 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
          {sessionPublishAuthorityLine('this allocation', hostName)}
        </p>
      ) : null}

      <div className="space-y-4">
        {session.state === 'DRAFT' && mayHost ? (
          <AppCard>
            <Eyebrow>Open the trade</Eyebrow>
            <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
              Opening locks the envelope and turns on the link. Everyone spends their share from
              their own device.
            </p>
            <div className="mt-4">
              <PrimaryButton
                disabled={busy}
                onClick={() =>
                  void run(() => openForParticipants(session, userId ?? '', userName ?? 'Owner'))
                }
              >
                {busy ? 'Opening…' : 'Open for the room'}
              </PrimaryButton>
            </div>
          </AppCard>
        ) : null}

        {session.state !== 'DRAFT' ? (
          <AppCard>
            <Eyebrow>Invite</Eyebrow>
            <p className="mt-2 break-all rounded-md bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]">
              {joinUrl}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <SecondaryButton onClick={() => void copyLink()}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy link'}
              </SecondaryButton>
              <SecondaryButton onClick={() => window.open(joinUrl, '_blank')}>
                <ExternalLink className="h-4 w-4" /> Open as participant
              </SecondaryButton>
            </div>
          </AppCard>
        ) : null}

        {session.state === 'CRITERIA_FINALIZED' && mayHost ? (
          <AppCard>
            <Eyebrow>Spending</Eyebrow>
            <p className="mt-2 font-heading text-2xl font-semibold text-gray-900 dark:text-[var(--text-pri)]">
              {submitted}
              <span className="ml-2 align-middle font-body text-sm font-normal text-gray-500 dark:text-[var(--text-ter)]">
                {submitted === 1 ? 'person has' : 'people have'} spent their share
              </span>
            </p>
            <div className="mt-4">
              <PrimaryButton
                disabled={busy || submitted === 0}
                onClick={() => void run(() => closeAndComputeResults(session))}
              >
                {busy ? 'Closing…' : 'Close & compute the split'}
              </PrimaryButton>
            </div>
          </AppCard>
        ) : null}

        {hasResult ? (
          <AppCard className="!p-0">
            <div className="px-5 pt-5">
              <Eyebrow>The split</Eyebrow>
            </div>
            <ul className="mt-3">
              {split.map((row) => (
                <li
                  key={row.id}
                  className="border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                >
                  <div className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                      {row.name}
                    </span>
                    <span className="shrink-0 font-mono text-xs text-gray-600 dark:text-[var(--text-sec)]">
                      {row.amount != null
                        ? `${unit}${row.amount.toLocaleString()}`
                        : `${Math.round(row.share * 100)}%`}
                    </span>
                  </div>
                  {/* The bar is the argument: relative share, at a glance. */}
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-[var(--surface-raised)]">
                    <div
                      className="h-full rounded-full bg-electric-mustard-500"
                      style={{ width: `${Math.max(2, Math.round(row.share * 100))}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
            {session.state === 'RUN_COMPLETE' ? (
              mayHost ? (
                <div className="px-5 py-4">
                  <PrimaryButton disabled={busy} onClick={() => void run(() => publishResults(session.id))}>
                    {busy ? 'Publishing…' : 'Publish the split'}
                  </PrimaryButton>
                  <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                    Publishing puts the split on the record — with who spent what behind it, so the
                    approval memo writes itself.
                  </p>
                </div>
              ) : null
            ) : (
              <div className="px-5 py-4">
                <StatusChip label="Published" tone="green" />
              </div>
            )}
          </AppCard>
        ) : null}

        {error ? (
          <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
            {error}
          </p>
        ) : null}
      </div>
    </AppShell>
  );
}
